import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import { DOC_SECTION_SEARCH_PARAM } from "@/lib/portal/doc-anchor";
import { getGlofoxApiReferenceHref } from "@/lib/portal/glofox-routes";
import {
  searchGlofoxDocs,
  snippetAroundMatch,
  tokenizeQuery,
  type GlofoxSearchEntry,
} from "@/lib/portal/glofox-search";

const SUGGESTIONS = [
  { label: "Authentication", query: "authentication" },
  { label: "Webhooks", query: "webhooks" },
  { label: "Bookings", query: "bookings" },
] as const;

function resultKey(entry: GlofoxSearchEntry, index: number): string {
  return `${entry.route}:${entry.anchor ?? "page"}:${index}`;
}

function openResult(
  entry: GlofoxSearchEntry,
  navigate: ReturnType<typeof useNavigate>,
) {
  if (entry.route.endsWith(".html")) {
    window.location.assign(getGlofoxApiReferenceHref());
    return;
  }

  if (entry.anchor) {
    navigate({
      pathname: entry.route,
      search: `?${DOC_SECTION_SEARCH_PARAM}=${encodeURIComponent(entry.anchor)}`,
    });
    return;
  }

  navigate(entry.route);
}

/** Highlight query tokens in plain text (React nodes — no HTML injection). */
function highlightMatches(text: string, query: string): ReactNode {
  const tokens = tokenizeQuery(query);
  if (tokens.length === 0 || !text) {
    return text;
  }

  const pattern = new RegExp(
    `(${tokens.map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`,
    "gi",
  );
  const parts = text.split(pattern);
  if (parts.length === 1) {
    return text;
  }

  return parts.map((part, index) => {
    const isMatch = tokens.some((token) => part.toLowerCase() === token);
    return isMatch ? (
      <mark key={`${part}-${index}`} className="gf-doc-search__mark">
        {part}
      </mark>
    ) : (
      <span key={`${part}-${index}`}>{part}</span>
    );
  });
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT" ||
    target.isContentEditable
  ) {
    return true;
  }
  return Boolean(target.closest("[contenteditable='true'], [role='textbox']"));
}

function clampIndex(index: number, length: number): number {
  if (length <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(index, length - 1));
}

export function GlofoxDocSearch() {
  const navigate = useNavigate();
  const listId = useId();
  const titleId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeOptionRef = useRef<HTMLLIElement | null>(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const trimmed = query.trim();
  const searchResponse = trimmed
    ? searchGlofoxDocs(query)
    : { hits: [], total: 0, truncated: false };
  const results = searchResponse.hits.map((hit) => hit.entry);
  const showIdle = open && !trimmed;
  const showResults = open && trimmed.length > 0;
  const optionCount = showIdle
    ? SUGGESTIONS.length
    : showResults
      ? results.length
      : 0;
  const safeActiveIndex = clampIndex(activeIndex, optionCount);
  const activeOptionId =
    showIdle && SUGGESTIONS[safeActiveIndex]
      ? `${listId}-suggestion-${safeActiveIndex}`
      : showResults && results[safeActiveIndex]
        ? `${listId}-option-${safeActiveIndex}`
        : undefined;
  const statusMessage = showIdle
    ? "Quick search suggestions"
    : showResults && results.length === 0
      ? `No results for “${trimmed}”`
      : showResults
        ? searchResponse.truncated
          ? `Showing ${results.length} of ${searchResponse.total} results`
          : `${results.length} result${results.length === 1 ? "" : "s"}`
        : "";
  const listboxLabel = showIdle ? "Quick search suggestions" : "Search results";

  useEffect(() => {
    function onGlobalKey(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        if (isTypingTarget(event.target) && event.target !== inputRef.current) {
          return;
        }
        event.preventDefault();
        openSearch();
        return;
      }

      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      if (isTypingTarget(event.target)) {
        return;
      }
      const active = document.activeElement;
      const isSearchTrigger =
        active instanceof HTMLElement &&
        (active === triggerRef.current ||
          Boolean(active.closest('[data-testid="gf-doc-search-trigger"]')));
      if (
        active instanceof HTMLElement &&
        active !== document.body &&
        active !== document.documentElement &&
        !isSearchTrigger &&
        (active.tagName === "A" ||
          active.tagName === "BUTTON" ||
          active.getAttribute("role") === "button" ||
          active.getAttribute("role") === "menuitem" ||
          (active.tabIndex >= 0 && active !== inputRef.current))
      ) {
        return;
      }

      event.preventDefault();
      openSearch();
    }
    window.addEventListener("keydown", onGlobalKey);
    return () => window.removeEventListener("keydown", onGlobalKey);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    const node = activeOptionRef.current;
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ block: "nearest" });
    }
  }, [safeActiveIndex, open, showIdle, results.length]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  function openSearch() {
    setOpen(true);
    setActiveIndex(0);
  }

  function closeSearch() {
    setOpen(false);
    setQuery("");
    setActiveIndex(0);
    requestAnimationFrame(() => {
      triggerRef.current?.focus();
    });
  }

  function clearQuery() {
    setQuery("");
    setActiveIndex(0);
    inputRef.current?.focus();
  }

  function select(entry: GlofoxSearchEntry) {
    setQuery("");
    setOpen(false);
    setActiveIndex(0);
    openResult(entry, navigate);
  }

  function applySuggestion(value: string) {
    setQuery(value);
    setActiveIndex(0);
    inputRef.current?.focus();
  }

  function onKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      if (query) {
        clearQuery();
        return;
      }
      closeSearch();
      return;
    }

    if (optionCount === 0) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) =>
        Math.min(clampIndex(index, optionCount) + 1, optionCount - 1),
      );
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(clampIndex(index, optionCount) - 1, 0));
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      setActiveIndex(0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      setActiveIndex(optionCount - 1);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (showIdle && SUGGESTIONS[safeActiveIndex]) {
        applySuggestion(SUGGESTIONS[safeActiveIndex].query);
        return;
      }
      if (showResults && results[safeActiveIndex]) {
        select(results[safeActiveIndex]);
      }
    }
  }

  const modal =
    open && typeof document !== "undefined"
      ? createPortal(
          <div className="gf-doc-search-modal" data-testid="gf-doc-search-modal">
            <button
              type="button"
              className="gf-doc-search-modal__backdrop"
              aria-label="Close search"
              data-testid="gf-doc-search-backdrop"
              tabIndex={-1}
              onClick={closeSearch}
            />
            <div
              ref={dialogRef}
              className="gf-doc-search-modal__dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              data-testid="gf-doc-search-dialog"
            >
              <div className="gf-doc-search-modal__header">
                <h2 id={titleId} className="sr-only">
                  Search docs
                </h2>
                <label
                  className="gf-doc-search-modal__field"
                  htmlFor={`${listId}-input`}
                >
                  <i className="ti ti-search" aria-hidden="true" />
                  <input
                    id={`${listId}-input`}
                    ref={inputRef}
                    type="search"
                    className="gf-doc-search__input"
                    placeholder="Search docs…"
                    value={query}
                    autoComplete="off"
                    spellCheck={false}
                    role="combobox"
                    aria-expanded={true}
                    aria-controls={`${listId}-list`}
                    aria-autocomplete="list"
                    aria-haspopup="listbox"
                    aria-activedescendant={activeOptionId}
                    data-testid="gf-doc-search-input"
                    onChange={(event) => {
                      setQuery(event.target.value);
                      setActiveIndex(0);
                    }}
                    onKeyDown={onKeyDown}
                  />
                  {query ? (
                    <button
                      type="button"
                      className="gf-doc-search__clear"
                      aria-label="Clear search"
                      data-testid="gf-doc-search-clear"
                      tabIndex={-1}
                      onClick={clearQuery}
                    >
                      <i className="ti ti-x" aria-hidden="true" />
                    </button>
                  ) : null}
                </label>
                <button
                  type="button"
                  className="gf-doc-search-modal__close"
                  aria-label="Close search"
                  data-testid="gf-doc-search-close"
                  onClick={closeSearch}
                >
                  <i className="ti ti-x" aria-hidden="true" />
                </button>
              </div>

              <div
                className="gf-doc-search__status sr-only"
                role="status"
                aria-live="polite"
                aria-atomic="true"
                data-testid="gf-doc-search-status"
              >
                {statusMessage}
              </div>

              <div
                className="gf-doc-search-modal__body"
                data-testid="gf-doc-search-results"
              >
                {showIdle ? (
                  <div
                    className="gf-doc-search__idle"
                    data-testid="gf-doc-search-idle"
                  >
                    <p
                      className="gf-doc-search__idle-title"
                      id={`${listId}-idle-title`}
                    >
                      Quick search
                    </p>
                    <ul
                      className="gf-doc-search__suggestions"
                      id={`${listId}-list`}
                      role="listbox"
                      aria-labelledby={`${listId}-idle-title`}
                    >
                      {SUGGESTIONS.map((item, index) => {
                        const isActive = index === safeActiveIndex;
                        return (
                          <li
                            key={item.query}
                            id={`${listId}-suggestion-${index}`}
                            ref={isActive ? activeOptionRef : undefined}
                            role="option"
                            aria-selected={isActive}
                            className={`gf-doc-search__suggestion${isActive ? " is-active" : ""}`}
                            data-testid="gf-doc-search-suggestion"
                            tabIndex={-1}
                            onMouseEnter={() => setActiveIndex(index)}
                            onMouseDown={(event) => {
                              event.preventDefault();
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                applySuggestion(item.query);
                              }
                            }}
                            onClick={() => applySuggestion(item.query)}
                          >
                            <i className="ti ti-arrow-right" aria-hidden="true" />
                            {item.label}
                          </li>
                        );
                      })}
                    </ul>
                    <p className="gf-doc-search__footer">
                      <span>
                        <kbd>↑</kbd>
                        <kbd>↓</kbd> navigate
                      </span>
                      <span>
                        <kbd>Enter</kbd> open
                      </span>
                      <span>
                        <kbd>Esc</kbd> close
                      </span>
                      <span className="gf-doc-search__footer-mod">
                        <kbd>⌘</kbd>
                        <kbd>K</kbd> search
                      </span>
                    </p>
                  </div>
                ) : null}

                {showResults && results.length === 0 ? (
                  <div
                    className="gf-doc-search__empty"
                    id={`${listId}-list`}
                    role="listbox"
                    aria-label={listboxLabel}
                    data-testid="gf-doc-search-empty"
                  >
                    <i className="ti ti-search-off" aria-hidden="true" />
                    <strong>No results for “{trimmed}”</strong>
                    <p>Try a page title, heading, or API term.</p>
                  </div>
                ) : null}

                {showResults && results.length > 0 ? (
                  <>
                    <p className="gf-doc-search__count" id={`${listId}-count`}>
                      {searchResponse.truncated
                        ? `Showing ${results.length} of ${searchResponse.total}`
                        : `${results.length} result${results.length === 1 ? "" : "s"}`}
                    </p>
                    <ul
                      className="gf-doc-search__list"
                      id={`${listId}-list`}
                      role="listbox"
                      aria-labelledby={`${listId}-count`}
                    >
                      {results.map((entry, index) => {
                        const isSection = Boolean(entry.section);
                        const isActive = index === safeActiveIndex;
                        const snippet = snippetAroundMatch(entry.text, query);
                        return (
                          <li
                            key={resultKey(entry, index)}
                            id={`${listId}-option-${index}`}
                            ref={isActive ? activeOptionRef : undefined}
                            role="option"
                            aria-selected={isActive}
                            className={`gf-doc-search__result${isActive ? " is-active" : ""}`}
                            data-testid="gf-doc-search-result"
                            tabIndex={-1}
                            onMouseEnter={() => setActiveIndex(index)}
                            onMouseDown={(event) => {
                              event.preventDefault();
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                select(entry);
                              }
                            }}
                            onClick={() => select(entry)}
                          >
                            <span
                              className={`gf-doc-search__result-icon${isSection ? " is-section" : ""}`}
                              aria-hidden="true"
                            >
                              <i
                                className={`ti ${isSection ? "ti-hash" : "ti-file-text"}`}
                              />
                            </span>
                            <span className="gf-doc-search__result-body">
                              <span className="gf-doc-search__result-heading">
                                {highlightMatches(
                                  isSection
                                    ? (entry.section ?? entry.title)
                                    : entry.title,
                                  query,
                                )}
                              </span>
                              <span className="gf-doc-search__result-meta">
                                {isSection ? (
                                  <>
                                    <span className="gf-doc-search__crumb">
                                      {entry.title}
                                    </span>
                                    <span aria-hidden="true"> · </span>
                                    <span>Section</span>
                                  </>
                                ) : (
                                  <span>Page</span>
                                )}
                              </span>
                              <span className="gf-doc-search__result-text">
                                {highlightMatches(snippet, query)}
                              </span>
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                    <p className="gf-doc-search__footer">
                      <span>
                        <kbd>↑</kbd>
                        <kbd>↓</kbd> navigate
                      </span>
                      <span>
                        <kbd>Enter</kbd> open
                      </span>
                      <span>
                        <kbd>Esc</kbd> close
                      </span>
                    </p>
                  </>
                ) : null}
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      className="gf-doc-search"
      data-testid="gf-doc-search"
      data-expanded={open ? "true" : "false"}
    >
      <button
        ref={triggerRef}
        type="button"
        className="gf-doc-search__trigger"
        aria-label="Search docs"
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid="gf-doc-search-trigger"
        onClick={openSearch}
      >
        <i className="ti ti-search" aria-hidden="true" />
        <span className="gf-doc-search__trigger-label">Search docs…</span>
        <kbd className="gf-doc-search__hint" aria-hidden="true">
          /
        </kbd>
      </button>
      {modal}
    </div>
  );
}
